import cx from "classnames";
import { CheckIconSvg } from "components/icons/CheckIconSvg";
import { ChevronSvg, PagePointDirections } from "components/icons/ChevronSvg";
import { LocalizationContext } from "context/localization";
import { useTranslationContext } from "context/translation";
import { Image, ResponsiveImageType } from "react-datocms";
import { FC, useContext, useState } from "react";
import {
  BundleItemRecord,
  ProductBundleRecord,
} from "services/datocms/generated";
import st from "./product-bundle.module.scss";
import clsx from "classnames";

interface ProductBundleProps {
  bundleItems: BundleItemRecord[];
  image?: ResponsiveImageType | null;
  onSelect: () => void;
  selected: boolean;
  title: ProductBundleRecord["title"];
  bundlePrice: number;
  analyticsAttributes: Record<string, string>;
  lowestPriceText: ProductBundleRecord["lowestPriceText"];
  loading?: boolean;
}

export const ProductBundle: FC<ProductBundleProps> = ({
  bundleItems,
  image,
  onSelect,
  selected,
  title,
  bundlePrice,
  analyticsAttributes,
  lowestPriceText,
  loading = false,
}) => {
  const { formatPriceByLocale } = useContext(LocalizationContext);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const { showMore, showLess } = useTranslationContext();

  const toggleShowOptions = () => setShowMoreOptions(!showMoreOptions);
  const formattedPrice = formatPriceByLocale(bundlePrice);

  return (
    <div
      className={cx(st.container, { [st.selected]: selected })}
      onClick={onSelect}
      {...analyticsAttributes}
    >
      {selected && (
        <div className={st.checkContainer}>
          <CheckIconSvg />
        </div>
      )}
      <div className={st.innerContainer}>
        <div className={st.topContainer}>
          <div className={st.machineInfoContainer}>
            <h2>{title}</h2>
            {loading ? (
              <div className="rounded-md w-[180px] h-[36.5px] bg-gray-200 animate-pulse-opacity" />
            ) : (
              <span>{formattedPrice}</span>
            )}
          </div>
          {image && (
            <div className={clsx(st.productImage, st.large)}>
              <Image
                data={image}
                objectFit="contain"
                className={st.imageContainer}
              />
            </div>
          )}
        </div>
        <div className={st.lowestPriceContainer}>
          {lowestPriceText && lowestPriceText}
        </div>
        <div className={st.bottomContainer}>
          {selected &&
            bundleItems
              .slice(0, showMoreOptions ? bundleItems.length : 2)
              .map((bundleItem, index) => (
                <div className={st.productContainer} key={index}>
                  <div className="absolute -top-1 -left-1 h-[18px] w-[18px] bg-sodaStreamBlue rounded-full flex items-center justify-center">
                    <span className="text-white font-[Poppins] text-[12px] font-[700]">
                      {bundleItem.amount}
                    </span>
                  </div>
                  {bundleItem.image?.responsiveImage && (
                    <div className={clsx(st.productImage, st.small)}>
                      <Image
                        data={bundleItem.image?.responsiveImage}
                        objectFit="contain"
                        className={st.imageContainer}
                      />
                    </div>
                  )}
                  <span>{bundleItem?.title || ""}</span>
                </div>
              ))}
          {selected && bundleItems.length > 2 && (
            <button
              className="flex items-center gap-[3px] text-start text-[#005D6E] font-[Poppins,sans-serif] text-[12px] font-[500] underline"
              onClick={toggleShowOptions}
            >
              {showMoreOptions ? showLess : showMore}
              {showMoreOptions ? (
                <ChevronSvg pointDirection={PagePointDirections.up} />
              ) : (
                <ChevronSvg pointDirection={PagePointDirections.down} />
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
